import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { homeSmartEntryPath, saveSiteSession } from '../../utils/brandSession';

const API = process.env.REACT_APP_BACKEND_URL;
const BLUE = '#1947C7';

const ssoExchangePromises = new Map();
function exchangeSsoOnce(sso) {
  if (ssoExchangePromises.has(sso)) return ssoExchangePromises.get(sso);
  const promise = (async () => {
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(sso));
    const key = 'hs_sso_result_' + Array.from(new Uint8Array(hash), b => b.toString(16).padStart(2, '0')).join('');
    const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (cached) {
      if (localStorage.getItem('hw_token') === cached.access_token) return cached;
      throw new Error('Link già utilizzato. Accedi al tuo account o richiedi un nuovo collegamento.');
    }
    const { data } = await axios.post(`${API}/api/portal/terzi/session`, { sso_token: sso });
    const verified = await axios.get(`${API}/api/auth/verify`, {headers: {Authorization: `Bearer ${data.access_token}`}});
    const user = verified.data.user;
    if (!user?.is_external_tenant || (data.tenant_id && user.external_tenant_id !== data.tenant_id)) throw new Error('Collegamento non valido per questo account.');
    const result = {...data, user};
    sessionStorage.setItem(key, JSON.stringify(result));
    return result;
  })().finally(() => ssoExchangePromises.delete(sso));
  ssoExchangePromises.set(sso, promise);
  return promise;
}

export default function HomeSmartPortal() {
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    const init = async () => {
      // StrictMode may dispose an effect before its asynchronous work begins.
      await Promise.resolve();
      if (!active) return;
      const params = new URLSearchParams(window.location.search);
      const sso = params.get('sso');
      if (sso) {
        try {
          const data = await exchangeSsoOnce(sso);
          if (!active) return;
          saveSiteSession(data.access_token, data.user, 'home-smart');
          window.location.replace(homeSmartEntryPath(data.redirect));
          return;
        } catch (e) {
          if (active) setError('Link di accesso non valido o scaduto. Accedi al tuo account o richiedi un nuovo collegamento.');
          return;
        }
      }
      if (localStorage.getItem('hw_token')) {
        window.location.replace(homeSmartEntryPath());
        return;
      }
      setError('Nessuna sessione attiva.');
    };
    init();
    return () => { active = false; };
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="hs-portal-error">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-10 max-w-md text-center">
          <img src="/home-smart-logo.png" alt="Home Smart" className="h-24 w-24 mx-auto mb-4 object-contain" data-testid="hs-portal-error-logo" />
          <h1 className="text-xl font-bold text-slate-800 mb-2" data-testid="hs-portal-error-title">Accesso non riuscito</h1>
          <p className="text-slate-600 text-sm" data-testid="hs-portal-error-message">{error}</p>
          <p className="text-slate-400 text-xs mt-4" data-testid="hs-portal-error-help">Riapri Home Smart dal sito o dal portale partner da cui hai attivato il servizio.</p>
          <div className="mt-5 flex justify-center gap-3">
            <Link to="/home-smart/accedi" className="rounded-md bg-[#1947C7] px-4 py-2 text-sm font-bold text-white hover:bg-[#1337a3]" data-testid="hs-portal-login-link">Accedi</Link>
            <Link to="/home-smart" className="rounded-md border border-[#dbe5f3] px-4 py-2 text-sm font-bold text-[#1947C7] hover:bg-[#f0f4fa]" data-testid="hs-portal-home-link">Home Smart</Link>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="hs-portal-loading">
      <div className="text-center">
        <img src="/home-smart-logo.png" alt="Home Smart" className="h-28 w-28 mx-auto mb-3 object-contain animate-pulse" data-testid="hs-portal-loading-logo" />
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: BLUE }} />
        <p className="mt-3 text-slate-600 text-sm" data-testid="hs-portal-loading-message">Accesso a Home Smart in corso…</p>
      </div>
    </div>
  );
}
